'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  INTL_AUDIO_MAX_BYTES,
  INTL_AUDIO_MAX_SECONDS,
  IntlAudioPartKey,
} from '@/lib/recruitment/intl-application';

/** One recording of an audio step (agent script parts, Marketing recorded answer). */
export type AudioPartDef<K extends string = string> = {
  key: K;
  title: string;
  instructions: string;
  scriptText: string | null;
  minSeconds: number;
  maxSeconds: number;
};

/** Length of an audio file in seconds, read from its metadata; null when the browser cannot tell. */
function readAudioDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    audio.preload = 'metadata';
    audio.onloadedmetadata = () =>
      done(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null);
    audio.onerror = () => done(null);
    audio.src = url;
  });
}

type RecordState = 'idle' | 'recording' | 'recorded' | 'uploading' | 'uploaded';

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

// audio/mp4 last - the only one iOS Safari supports.
function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported?.(c)) return c;
  }
  return undefined;
}

function extensionForType(type: string): string {
  if (type.includes('ogg')) return 'ogg';
  if (type.includes('mp4')) return 'mp4';
  return 'webm';
}

export function AudioRecorderCard<K extends string = IntlAudioPartKey>({
  token,
  part,
  uploadedDurationSeconds,
  onUploaded,
  onExpired,
  allowFileUpload,
}: {
  token: string;
  part: AudioPartDef<K>;
  /** Duration from the bootstrap when this part was already recorded (resume). */
  uploadedDurationSeconds: number | null;
  onUploaded: (part: K, durationSeconds: number, completedSteps: string[]) => void;
  onExpired: () => void;
  /** Offer "upload an audio file instead" when recording does not work on the device. */
  allowFileUpload?: boolean;
}) {
  const [recordState, setRecordState] = useState<RecordState>(
    uploadedDurationSeconds !== null ? 'uploaded' : 'idle'
  );
  const [elapsed, setElapsed] = useState(0);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [uploadedDuration, setUploadedDuration] = useState<number | null>(uploadedDurationSeconds);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [maxReached, setMaxReached] = useState(false);
  const [error, setError] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const elapsedRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** File name of an uploaded audio file (the recorder names its own blobs). */
  const uploadNameRef = useRef<string | null>(null);

  // Cleanup media + timers + object URLs on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  }, []);

  const beginRecording = useCallback(async () => {
    setError('');
    setMaxReached(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setRecordedBlob(null);
    uploadNameRef.current = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        setRecordedBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        setRecordedDuration(elapsedRef.current);
        setRecordState('recorded');
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };

      recorder.start();
      elapsedRef.current = 0;
      setElapsed(0);
      setRecordState('recording');
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
        if (elapsedRef.current >= INTL_AUDIO_MAX_SECONDS) {
          setMaxReached(true);
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      const msg =
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Microphone permission was denied. Please allow microphone access for this page and try again.'
          : 'Could not start recording. A working microphone and a secure (HTTPS) connection are required.';
      setError(msg);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [previewUrl, stopRecording]);

  // Fallback for devices where recording fails: the applicant picks an audio
  // file, listens to it and confirms it exactly like a recording.
  const acceptAudioFile = useCallback(
    async (file: File) => {
      setError('');
      setMaxReached(false);
      if (file.size > INTL_AUDIO_MAX_BYTES) {
        setError('The audio file is larger than 25 MB. Please choose a smaller file.');
        return;
      }
      const duration = await readAudioDuration(file);
      if (duration === null) {
        setError('This file could not be read as audio. Please upload an MP3, M4A, or WAV file.');
        return;
      }
      if (duration > part.maxSeconds) {
        setError(`The audio is longer than ${Math.round(part.maxSeconds / 60)} minutes. Please upload a shorter one.`);
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      uploadNameRef.current = file.name || null;
      setRecordedBlob(file);
      setPreviewUrl(URL.createObjectURL(file));
      setRecordedDuration(Math.max(1, Math.round(duration)));
      setRecordState('recorded');
    },
    [part.maxSeconds, previewUrl]
  );

  const discardRecording = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    uploadNameRef.current = null;
    setRecordedBlob(null);
    setRecordedDuration(0);
    setMaxReached(false);
    setError('');
    setRecordState('idle');
  }, [previewUrl]);

  const uploadRecording = useCallback(async () => {
    if (!recordedBlob) return;
    setError('');
    setRecordState('uploading');
    try {
      const duration = Math.max(1, recordedDuration);
      const ext = extensionForType(recordedBlob.type || '');
      const formData = new FormData();
      formData.append('audio', recordedBlob, uploadNameRef.current || `${part.key}.${ext}`);
      formData.append('durationSeconds', String(duration));
      const res = await fetch(`/api/public/intl-recruitment/${token}/audio/${part.key}`, {
        method: 'POST',
        body: formData,
      });
      if (res.status === 410) {
        onExpired();
        return;
      }
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(d.error || 'Upload failed. Please try again.');
        setRecordState('recorded');
        return;
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setRecordedBlob(null);
      setUploadedDuration(Number(d.durationSeconds) || duration);
      setRecordState('uploaded');
      onUploaded(
        part.key,
        Number(d.durationSeconds) || duration,
        Array.isArray(d.completedSteps) ? d.completedSteps : []
      );
    } catch {
      setError('Upload failed. Check your connection and try again.');
      setRecordState('recorded');
    }
  }, [recordedBlob, recordedDuration, part.key, token, previewUrl, onUploaded, onExpired]);

  const tooShort = recordState === 'recorded' && recordedDuration < part.minSeconds;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
      <p className="text-sm font-semibold text-white">{part.title}</p>
      <p className="text-xs text-gray-400 mt-1">{part.instructions}</p>

      {part.scriptText && (
        <div className="mt-3 rounded-xl bg-gray-800 border border-gray-700 px-4 py-3">
          {part.scriptText.split('\n\n').map((paragraph, i) => (
            <p key={i} className={`text-sm text-gray-200 leading-relaxed ${i > 0 ? 'mt-2.5' : ''}`}>
              {paragraph}
            </p>
          ))}
        </div>
      )}

      <div className="mt-3">
        {recordState === 'idle' && (
          <button
            type="button"
            onClick={() => void beginRecording()}
            className="btn btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2 h-auto"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            Start recording
          </button>
        )}

        {recordState === 'recording' && (
          <div className="rounded-xl border border-red-800 bg-red-900/20 px-4 py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-red-300 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                Recording…
              </p>
              <p className="text-sm font-mono text-red-200">
                {formatClock(elapsed)} / {formatClock(INTL_AUDIO_MAX_SECONDS)}
              </p>
            </div>
            <button
              type="button"
              onClick={stopRecording}
              className="mt-3 w-full py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm font-medium transition-colors"
            >
              Stop recording
            </button>
          </div>
        )}

        {(recordState === 'recorded' || recordState === 'uploading') && previewUrl && (
          <div className="rounded-xl border border-gray-700 bg-gray-800 px-4 py-3 space-y-3">
            {maxReached && (
              <p className="text-xs text-amber-300">Maximum length reached - the recording stopped automatically.</p>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-400">Recorded · {formatClock(recordedDuration)}</p>
            </div>
            <audio controls src={previewUrl} className="w-full" preload="metadata" />
            {tooShort && (
              <p className="text-xs text-amber-300">
                This recording is too short - please record at least {part.minSeconds} seconds.
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={discardRecording}
                disabled={recordState === 'uploading'}
                className="flex-1 py-2.5 rounded-xl bg-gray-900 hover:bg-gray-700 border border-gray-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
              >
                Re-record
              </button>
              <button
                type="button"
                onClick={() => void uploadRecording()}
                disabled={recordState === 'uploading' || tooShort}
                className="btn btn-primary flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed h-auto"
              >
                {recordState === 'uploading' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading…
                  </>
                ) : (
                  'Use this recording'
                )}
              </button>
            </div>
          </div>
        )}

        {recordState === 'uploaded' && (
          <div className="rounded-xl border border-emerald-800 bg-emerald-900/20 px-4 py-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-emerald-200 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                Recording saved{uploadedDuration ? ` · ${formatClock(uploadedDuration)}` : ''}
              </p>
              <button
                type="button"
                onClick={discardRecording}
                className="text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 hover:border-gray-500 rounded-lg px-3 py-1.5 transition-colors flex-shrink-0"
              >
                Re-record
              </button>
            </div>
            <audio
              controls
              src={`/api/public/intl-recruitment/${token}/audio/${part.key}?play=1`}
              className="w-full"
              preload="none"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-3 py-2 text-xs">
          {error}
        </div>
      )}

      {allowFileUpload && recordState === 'idle' && (
        <div className="mt-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.m4a,.wav,.ogg,.webm,.aac"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void acceptAudioFile(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-xs text-gray-400 hover:text-gray-200 underline underline-offset-2"
          >
            Recording does not work on this device? Upload an audio file instead
          </button>
        </div>
      )}
    </div>
  );
}
